// Demo organization data for org mode functionality
// Slimmed down: just Def Jam + Personal Workspace
// Def Jam member/role data lives in def-jam/organization.ts

import {
  Org,
  OrgMember,
  OrgCustomRole,
  OrgPermission,
} from '@equaliq/api-model';
import { availablePermissions } from '@/constants/organization.constants';
import { DEF_JAM_ORG_ID } from './common/demoFilters';
import {
  defJamCustomRoles,
  defJamInvitations,
  defJamMembers,
} from './def-jam/organization';

export { OrgPermission, availablePermissions };

export interface DemoProfile {
  displayName: string;
  firstName: string;
  lastName: string;
  email: string;
  accountType: string;
  profileImage?: string;
}

export interface DemoOrganization extends Org {
  colors?: {
    primary: string;
    secondary: string;
    accent: string;
  };
  branding?: {
    logo?: string;
    platformName?: string;
  };
  demoUser?: DemoProfile;
}

export type DemoOrganizationMember = OrgMember;

export interface DemoCustomRole extends OrgCustomRole {
  memberCount: number;
}

export const demoOrganizations: DemoOrganization[] = [
  {
    orgId: DEF_JAM_ORG_ID,
    name: 'Def Jam Recordings',
    type: 'record_label',
    memberCount: 7,
    primaryOwnerId: 'user-4',
    description: 'Major record label and hip-hop powerhouse',
    billingEmail: 'billing@defjam.com',
    createdAt: '2024-03-10T00:00:00Z',
    updatedAt: '2024-03-10T00:00:00Z',
    logoUrl: '/demo/def_jam_logo.png',
    userRole: 'custom',
    dealCount: 12,
    inviteCount: 3,
    roleCount: 4,
    colors: {
      primary: '#FF0000',
      secondary: '#DC143C',
      accent: '#8B0000',
    },
    branding: {
      logo: '/demo/def_jam_logo.png',
      platformName: 'Def Jam',
    },
    demoUser: {
      displayName: 'Tim Pithouse',
      firstName: 'Tim',
      lastName: 'Pithouse',
      email: 'tim.pithouse@umusic.com',
      accountType: 'General Manager',
      profileImage: '/demo/tim_pithouse.jpg',
    },
  },
];

export const getDemoInvitationsByOrgId = (orgId: string) => {
  if (orgId === DEF_JAM_ORG_ID) {
    return defJamInvitations;
  }

  return [];
};

export const getDemoOrganizationById = (orgId: string): DemoOrganization | undefined =>
  demoOrganizations.find((org) => org.orgId === orgId);

export const getDemoMembersByOrgId = (orgId: string): DemoOrganizationMember[] =>
  orgId === DEF_JAM_ORG_ID ? defJamMembers : [];

export const getDemoCustomRolesByOrgId = (orgId: string): DemoCustomRole[] =>
  orgId === DEF_JAM_ORG_ID ? defJamCustomRoles : [];

export const getCurrentUserRoleInOrg = (
  orgId: string,
  currentUserId: string = 'user-1',
): { role: string; customRoleName?: string } | null => {
  const members = getDemoMembersByOrgId(orgId);
  const userMembership = members.find((member) => member.userId === currentUserId);

  if (!userMembership) {
    return null;
  }

  return {
    role: userMembership.role,
    customRoleName: userMembership.customRoleName,
  };
};

export const demoUserProfile: DemoProfile = {
  displayName: 'Mayowa Arogundade',
  firstName: 'Mayowa',
  lastName: 'Arogundade',
  email: 'mayowa@example.com',
  accountType: 'Executive',
  profileImage: '/demo/mayowa_pic.jpeg',
};

export interface DemoDataRegistry {
  organizations: DemoOrganization[];
  members: DemoOrganizationMember[];
  customRoles: DemoCustomRole[];
  profile: DemoProfile;
}

export const demoDataRegistry: DemoDataRegistry = {
  organizations: demoOrganizations,
  members: defJamMembers,
  customRoles: defJamCustomRoles,
  profile: demoUserProfile,
};
